import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import { alphabetical, sort, title } from "radash";
import { Fade } from "react-awesome-reveal";
import { Popover, Select } from "antd";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
import { useLocalStorage } from "react-use";
import { useNavigate } from "react-router-dom";

import { styles } from "@evolved/constants";

import { formatDollars } from "../../../utils/format-dollars";

import { Void } from "components/image-placeholders";
import { useOpportunityStates } from "../../../data/use-opportunity-states";
import { useProductOpportunityCountsBy } from "../../../data/use-reports";
import { RangeSelect } from "../range-select";
import { WidgetHeader } from "../widget-header";
import { withSalesRole } from "../../../components/with-role";
import { WidgetContainer } from "../widget-container";
import { useOrganization } from "../../../data/use-organization";
import { convertRange } from "../convert-range";

const { CLICKABLE_STYLE } = styles;

export const filterOption = (input, option) =>
  (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const AccountsLink = ({ navigate }) => (
  <span onClick={() => navigate("/accounts")} style={CLICKABLE_STYLE}>
    accounts
  </span>
);

const OpportunitiesLink = ({ navigate }) => (
  <span onClick={() => navigate("/opportunities")} style={CLICKABLE_STYLE}>
    opportunities
  </span>
);

const ProductsLink = ({ navigate }) => (
  <span onClick={() => navigate("/products")} style={CLICKABLE_STYLE}>
    products
  </span>
);

export const useProductOpportunitiesState = (organization) => {
  const [isUser, setIsUser] = useLocalStorage(
    `${organization._id}.DASHBOARD.OPPORTUNITY_WIN_RATE.IS_USER`,
    true
  );

  const [sortOrder, setSortOrder] = useLocalStorage(
    `${organization._id}.DASHBOARD.OPPORTUNITY_WIN_RATE.SORT_ORDER`,
    "desc"
  );

  const sortTypes = ["closedRate", "wonValue", "name"];

  const [sortType, setSortType] = useLocalStorage(
    `${organization._id}.DASHBOARD.OPPORTUNITY_WIN_RATE.SORT_TYPE`,
    sortTypes[0]
  );

  const toggleSortType = () => {
    const currentIndex = sortTypes.indexOf(sortType);

    setSortType(sortTypes[(currentIndex + 1) % sortTypes.length]);
  };

  return {
    isUser,
    setIsUser,
    setSortOrder,
    sortOrder,
    sortType,
    toggleSortType,
  };
};

const Component = (props) => {
  const {
    setSortOrder,
    sortOrder,
    sortType,
    toggleSortType,

    factorType,
    showFactoredValue,
    hideDetails,

    showTotals,

    selectedStateIds,
    setSelectedStateIds,
  } = props;

  const opportunityStateStore = useOpportunityStates();
  const opportunityStates = opportunityStateStore.all();

  const wonStateIds = opportunityStates
    .filter(({ systemKey }) => systemKey === "WON")
    .map(({ _id }) => Number(_id));

  const {
    widgetFilter,
    widgetFilterLabel,
    setWidgetFilter,
    ...dateFilterOffsets
  } = props.dateRange;

  const productOpportunityCounts = useProductOpportunityCountsBy(
    "stateId",
    convertRange(widgetFilter)
  );

  const navigate = useNavigate();
  const organization = useOrganization();

  const counts = productOpportunityCounts;

  let totalFactoredValue = 0;
  let totalUnfactoredValue = 0;

  const reducedCounts = (counts ?? []).map(({ _id, name, counts }) => {
    const { count: wonCount, value: wonValue } = (counts ?? []).reduce(
      (acc, { stateId, count, value }) =>
        wonStateIds.includes(Number(stateId))
          ? { count: acc.count + count, value: acc.value + value }
          : acc,
      { count: 0, value: 0 }
    );

    const { count, value } = counts.reduce(
      (acc, { stateId, count, value }) => {
        if (organization.settings?.winRateOpportunityStateIds) {
          const opportunityState = opportunityStates.find(
            ({ _id }) => _id === stateId
          );

          if (["WON", "LOST"].includes(opportunityState?.systemKey)) {
            return {
              count: acc.count + count,
              value: acc.value + value,
            };
          }

          if (
            organization.settings?.winRateOpportunityStateIds.includes(stateId)
          ) {
            return {
              count: acc.count + count,
              value: acc.value + value,
            };
          }

          return acc;
        }

        return {
          count: acc.count + count,
          value: acc.value + value,
        };
      },
      { value: 0, count: 0 }
    );

    const numerator = factorType === "value" ? wonValue : wonCount;
    const denominator = factorType === "value" ? value : count;

    const factor = denominator === 0 ? 0 : numerator / denominator;

    const unfactoredValue = counts.reduce((acc, { stateId, value }) => {
      if (selectedStateIds.includes(stateId)) {
        return acc + value;
      }

      return acc;
    }, 0);

    const factoredValue = factor * unfactoredValue;

    totalUnfactoredValue = totalUnfactoredValue + unfactoredValue;
    totalFactoredValue = totalFactoredValue + factoredValue;

    return {
      _id,

      numerator,
      denominator,
      closeRate: factor,

      unfactoredValue,
      factoredValue,

      name,
    };
  }, []);

  let sortedCounts;

  if (["name"].includes(sortType)) {
    sortedCounts = alphabetical(
      reducedCounts,
      (count) => {
        return count[sortType];
      },
      sortOrder
    );
  } else {
    sortedCounts = sort(
      reducedCounts,
      (count) => {
        return count[sortType];
      },
      sortOrder === "desc"
    );
  }

  return (
    <div>
      <div
        style={{ alignItems: "center", display: "flex", marginBottom: "8px" }}
      >
        <div class="mr-auto">
          <WidgetHeader label={props.title ?? "Opportunity Win Rate"} />
          <div class="flex space-x-1 w-full">
            <div>Sort</div>
            {
              <div onClick={toggleSortType} style={styles.CLICKABLE_STYLE}>
                {title(sortType)}
              </div>
            }
            {sortOrder === "desc" ? (
              <div
                onClick={() => setSortOrder("asc")}
                style={styles.CLICKABLE_STYLE}
              >
                <DownOutlined />
              </div>
            ) : (
              <div
                onClick={() => setSortOrder("desc")}
                style={styles.CLICKABLE_STYLE}
              >
                <UpOutlined />
              </div>
            )}
          </div>
        </div>
        <div
        >
          <div
            style={{ alignItems: "center", display: "flex", marginBottom: "8px" }}
          >
            <RangeSelect
              tenses={["past", "all-time"]}
              {...{
                widgetFilter,
                widgetFilterLabel,
                setWidgetFilter,
                ...dateFilterOffsets,
              }}
            />
          </div>
          {selectedStateIds?.length ? (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Popover content={
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  {selectedStateIds.map(id => opportunityStateStore.byId(id)).filter(state => !!state).map(state => state.label).join(", ")}
                </div>
              }>
                <div >Based On Stages</div>

              </Popover>
            </div>

          ) : null}
        </div>
      </div>
      {showFactoredValue && setSelectedStateIds && (
        <div className="mb-4 flex">
          <Select
            filterOption={filterOption}
            mode="multiple"
            notFoundContent={"No match"}
            placeholder="Select at least one stage"
            optionFilterProp="children"
            showSearch
            options={opportunityStates.map((option) => ({
              value: option._id,
              label: option.label,
            }))}
            value={selectedStateIds}
            onChange={setSelectedStateIds}
            size="small"
            style={{ width: "100%" }}
          />
        </div>
      )}

      {isEmpty(counts) ? (
        <Fade>
          <Void
            primary={"No opportunities yet."}
            secondary={
              <>
                Go ahead and create some <ProductsLink navigate={navigate} />,{" "}
                <AccountsLink navigate={navigate} /> and{" "}
                <OpportunitiesLink navigate={navigate} />
              </>
            }
          />
        </Fade>
      ) : (
        <Fade>
          <div
            style={{ alignItems: "center", display: "flex", flexWrap: "wrap" }}
          >
            {map(
              sortedCounts,
              (
                {
                  _id,
                  name,

                  numerator,
                  denominator,
                  closeRate,

                  unfactoredValue,
                  factoredValue,
                },
                i
              ) => {
                return (
                  <div
                    key={_id}
                    style={{
                      ...(i >= 2 ? { marginTop: "16px" } : {}),
                      width: "50%",
                      padding: "8px",
                    }}
                  >
                    <div style={{ width: "max-content" }}>
                      <div className="flex">
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "black",
                          }}
                        >
                          {name}
                        </div>
                      </div>
                      <div>
                        <div
                          style={{ display: "flex", alignItems: "baseline" }}
                        >
                          <Popover
                            content={!hideDetails ? null : factorType === "value" ? (
                              <div
                                className="text-sm font-light"
                                style={{
                                  lineHeight: 1,
                                  marginLeft: "12px",
                                }}
                              >
                                {formatDollars(numerator)} /{" "}
                                {formatDollars(denominator)}
                              </div>
                            ) : (
                              <div
                                className="text-sm font-light"
                                style={{
                                  lineHeight: 1,
                                  marginLeft: "12px",
                                }}
                              >
                                {Math.round(numerator)} /{" "}
                                {Math.round(denominator)}
                              </div>
                            )}
                          >
                            <div
                              style={{
                                fontSize: "32px",
                                fontWeight: "600",
                                color: "#7F5196",
                                lineHeight: "40px",
                              }}
                            >
                              {Math.round(closeRate * 100)}
                              <span
                                style={{
                                  fontSize: "18px",
                                  lineHeight: "32px",
                                }}
                              >
                                %
                              </span>
                            </div>
                          </Popover>
                          {hideDetails ? null : factorType === "value" ? (
                            <div
                              className="text-sm font-light"
                              style={{
                                fontWeight: "600",
                                lineHeight: 1,
                                marginLeft: "12px",
                              }}
                            >
                              {formatDollars(numerator)} /{" "}
                              {formatDollars(denominator)}
                            </div>
                          ) : (
                            <div
                              className="text-sm font-light"
                              style={{
                                fontWeight: "600",
                                lineHeight: 1,
                                marginLeft: "12px",
                              }}
                            >
                              {Math.round(numerator)} /{" "}
                              {Math.round(denominator)}
                            </div>
                          )}
                          {showFactoredValue && (
                            <Popover
                              content={(
                                <div>
                                  {formatDollars(unfactoredValue)} X{" "}
                                  {Math.round(closeRate * 100)}%
                                </div>
                              )}
                            >
                              <div style={{ marginLeft: "16px", display: "flex", alignItems: "end" }}>
                                <div className="text-lg font-bold">
                                  {formatDollars(factoredValue)}
                                </div>
                              </div>
                            </Popover>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
            )}
            {showTotals ? (
              <div className="mt-2">
                <div className="text-lg font-bold">
                  Total = {formatDollars(totalUnfactoredValue)}
                </div>
                <div className="text-lg font-bold">
                  Factored = {formatDollars(totalFactoredValue)}
                </div>
              </div>
            ) : null}
          </div>
        </Fade>
      )}
    </div>
  );
};

export const OpportunityWinRate = withSalesRole((props) => {
  return (
    <WidgetContainer>
      <Component {...props} />
    </WidgetContainer>
  );
});
